import { ClipboardCheck } from "lucide-react";
import EmptyState from "@cle/components/EmptyState";
import RoundedCard from "@cle/components/RoundedCard";
import PendingReportCard, {
  type PendingReportingPeriodItem,
} from "@cle/components/pending-reports/PendingReportCard";
import PendingReportCardSkeleton from "@cle/components/pending-reports/PendingReportCardSkeleton";
import CurrentPeriodLessonsSummary from "./CurrentPeriodLessonsSummary";
import { mapAfventendeIndberetningToPendingItem } from "@cle/utils/reportingPeriodMappers";
import { useAfventendeIndberetninger } from "@/hooks/useAfventendeIndberetninger";

type PendingReportsListProps = {
  errorMessages: string[];
  isLoading: boolean;
  items: PendingReportingPeriodItem[];
};

const PendingReportsList = ({
  errorMessages,
  isLoading,
  items,
}: PendingReportsListProps) => {
  const hasErrors = errorMessages.length > 0;

  if (isLoading) {
    return (
      <>
        <span className="visually-hidden">
          Indlæser afventende indberetninger
        </span>
        <PendingReportCardSkeleton />
      </>
    );
  }

  if (items.length === 0 && !hasErrors) {
    return (
      <EmptyState
        variant="success"
        description="Husk stadig at optjene nok lektioner i den nuværende efteruddannelsesperiode, inden perioden udløber."
        footer={<CurrentPeriodLessonsSummary />}
        icon={<ClipboardCheck />}
        title="Ingen afventende indberetninger"
      />
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {errorMessages.map((message) => (
        <div key={message} className="alert alert-warning" role="alert">
          {message}
        </div>
      ))}
      {items.map((item) => (
        <PendingReportCard key={`${item.kind}-${item.id}`} item={item} />
      ))}
    </div>
  );
};

const PendingReportsContent = () => {
  const {
    data: afventendeIndberetninger = [],
    isError,
    isLoading,
  } = useAfventendeIndberetninger();

  const pendingItems = afventendeIndberetninger.map(
    mapAfventendeIndberetningToPendingItem,
  );
  const errorMessages = isError
    ? ["Afventende indberetninger kunne ikke indlæses lige nu."]
    : [];

  return (
    <RoundedCard
      className="mt-4"
      header="Afventende indberetninger"
      description="Hver opmærksom på at indberette inden fristen udløber."
    >
      <PendingReportsList
        errorMessages={errorMessages}
        isLoading={isLoading}
        items={pendingItems}
      />
    </RoundedCard>
  );
};

export default PendingReportsContent;
